import React, { useEffect, useLayoutEffect, useState, useMemo, useRef } from 'react';
import { useAtlasStore, AtlasState } from '../../../../store/atlasStore';
import ModalStyles from '../../styles/Modal.module.css';
import styles from '../../styles/JobDetailModal.module.css';
import { ConfirmationModal } from '../modals/ConfirmationModal';
import { SparkJob, SparkJobStatusUpdate } from '../../types/sparkTypes';
import { atlasClient } from '../../../../api/atlasClient';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import { formatEta, getJobDisplayStatus, useAnimatedProgress } from '../../hooks/useAnimatedProgress';

interface JobDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    jobFqn: string | null;
}

type BrowserLocales = string | string[] | undefined;

const getBrowserLocales = (): BrowserLocales => {
    if (typeof navigator === 'undefined') {
        return undefined;
    }
    if (Array.isArray(navigator.languages) && navigator.languages.length > 0) {
        return [...navigator.languages];
    }
    return navigator.language || undefined;
};

// Helper to format timestamp to absolute date/time string
const formatAbsoluteTime = (dateString: string | null): string => {
    if (!dateString) return 'N/A';
    try {
        const locales = getBrowserLocales();
        return new Date(dateString).toLocaleString(locales);
    } catch (e) {
        return 'Invalid Date';
    }
};

// Helper to format time for timeline entries
const formatTimelineTime = (timestamp: string): string => {
    try {
        const date = new Date(timestamp);
        const locales = getBrowserLocales();
        return date.toLocaleTimeString(locales, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch {
        return '';
    }
};

// Helper to format duration in seconds to human-readable string
const formatDuration = (seconds: number): string => {
    if (seconds < 60) {
        return `${seconds}s`;
    }
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
        return `${minutes}m ${seconds % 60}s`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
};

// Helper to convert snake_case to Title Case (e.g., "generate_revision" -> "Generate Revision")
const formatStageName = (name: string): string => {
    return name
        .split('_')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ');
};

const RUN_AGENT_STAGE = 'run_agent';

const splitScopeSegments = (scope?: string): { root: string; segments: string[] } => {
    if (!scope) {
        return { root: '', segments: [] };
    }
    const parts = scope.split('.').filter(Boolean);
    if (parts.length === 0) {
        return { root: '', segments: [] };
    }
    const [root, ...rest] = parts;
    const segments = rest.filter(segment => segment !== RUN_AGENT_STAGE);
    return { root, segments };
};

const isNumericSegment = (segment: string): boolean => /^\d+$/.test(segment);

const formatPrefixLabel = (segments: string[]): string => {
    if (segments.length === 0) {
        return '';
    }
    const last = segments[segments.length - 1];
    if (isNumericSegment(last)) {
        const num = Number(last) + 1;
        const parent = segments[segments.length - 2];
        if (parent === 'optimize') {
            return `Optimize candidate ${num}`;
        }
        return `Item ${num}`;
    }
    if (last === 'media') {
        // Include branch context if media is under a numbered segment (parallel branch)
        // e.g., ['optimize', '0', 'media'] -> 'Candidate 1 media'
        const branchIdx = segments.findIndex(isNumericSegment);
        if (branchIdx >= 0) {
            const branchNum = Number(segments[branchIdx]) + 1;
            return `Candidate ${branchNum} media`;
        }
        return 'Media';
    }
    return formatStageName(last);
};

interface TimelineEntry {
    label: string;
    depth: number;
    timestamp: string;
    isHeading: boolean;
    scopeKey?: string;
}

/**
 * Extract the branch key from a scope (e.g., "scout.optimize.0" from "scout.optimize.0.media.hero_image").
 * Returns empty string for scopes without a numeric branch segment.
 */
const getBranchKey = (scope: string): string => {
    const { root, segments } = splitScopeSegments(scope);
    for (let i = 0; i < segments.length; i++) {
        if (isNumericSegment(segments[i])) {
            // Found numeric segment, return scope up to and including it
            const branchSegments = segments.slice(0, i + 1);
            return `${root}.${branchSegments.join('.')}`;
        }
    }
    return ''; // No branch, global entry
};

/**
 * Extract the numeric branch index from a branch key (e.g., "scout.optimize.2" -> 2).
 * Returns -1 if no numeric index found.
 */
const getBranchIndex = (branchKey: string): number => {
    const parts = branchKey.split('.');
    for (let i = parts.length - 1; i >= 0; i--) {
        if (isNumericSegment(parts[i])) {
            return parseInt(parts[i], 10);
        }
    }
    return -1;
};

/**
 * Sort updates so that branch entries are grouped together.
 * - Global entries (no branch) stay in timestamp order relative to branch groups
 * - All entries from the same branch are grouped together
 * - Branches are ordered by their numeric index (candidate 1 before candidate 2)
 * - Within a branch, entries are sorted by timestamp
 */
const sortUpdatesByBranch = (updates: SparkJobStatusUpdate[]): SparkJobStatusUpdate[] => {
    // Track first timestamp for each branch (used for global entry positioning)
    const branchFirstTimestamp = new Map<string, number>();
    updates.forEach(update => {
        const key = getBranchKey(update.scope || '');
        if (key && !branchFirstTimestamp.has(key)) {
            branchFirstTimestamp.set(key, new Date(update.timestamp).getTime());
        }
    });

    // Find the earliest branch timestamp (for positioning global entries)
    const earliestBranchTime = branchFirstTimestamp.size > 0
        ? Math.min(...Array.from(branchFirstTimestamp.values()))
        : 0;

    return [...updates].sort((a, b) => {
        const keyA = getBranchKey(a.scope || '');
        const keyB = getBranchKey(b.scope || '');

        if (keyA === keyB) {
            // Same branch (or both global), sort by timestamp
            return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
        }

        // Global entries (empty key) sort by their own timestamp relative to branches
        if (!keyA || !keyB) {
            const timeA = keyA ? earliestBranchTime : new Date(a.timestamp).getTime();
            const timeB = keyB ? earliestBranchTime : new Date(b.timestamp).getTime();
            if (timeA !== timeB) {
                return timeA - timeB;
            }
            // Global before branch if same time
            return keyA ? 1 : -1;
        }

        // Both are branches - sort by numeric index for deterministic ordering
        const indexA = getBranchIndex(keyA);
        const indexB = getBranchIndex(keyB);
        if (indexA !== indexB) {
            return indexA - indexB;
        }

        // Same index (shouldn't happen), fall back to key comparison
        return keyA.localeCompare(keyB);
    });
};

const buildTimelineEntries = (updates: SparkJobStatusUpdate[]): TimelineEntry[] => {
    const entries: TimelineEntry[] = [];
    const seenPrefixes = new Set<string>();

    // Filter out silent updates from user-visible timeline
    const visibleUpdates = updates.filter(update => !update.silent);

    // Sort updates so branch entries are grouped together
    const sortedUpdates = sortUpdatesByBranch(visibleUpdates);

    sortedUpdates.forEach(update => {
        if (!update.scope || update.scope === 'overall') {
            entries.push({
                label: update.message,
                depth: 0,
                timestamp: update.timestamp,
                isHeading: false,
                scopeKey: update.scope || undefined,
            });
            return;
        }

        const { root, segments } = splitScopeSegments(update.scope);
        if (segments.length === 0) {
            entries.push({
                label: update.message,
                depth: 0,
                timestamp: update.timestamp,
                isHeading: false,
                scopeKey: root,
            });
            return;
        }

        const ancestors = segments.slice(0, -1);
        ancestors.forEach((_, idx) => {
            const prefixSegments = segments.slice(0, idx + 1);
            const prefixKey = `${root}.${prefixSegments.join('.')}`;
            if (!seenPrefixes.has(prefixKey)) {
                seenPrefixes.add(prefixKey);
                entries.push({
                    label: formatPrefixLabel(prefixSegments),
                    depth: idx,
                    timestamp: update.timestamp,
                    isHeading: true,
                    scopeKey: `${root}.${prefixSegments.join('.')}`,
                });
            }
        });

        entries.push({
            label: update.message,
            depth: Math.max(segments.length - 1, 0),
            timestamp: update.timestamp,
            isHeading: false,
            scopeKey: `${root}.${segments.join('.')}`,
        });
    });

    return entries;
};

const buildActiveScopes = (updates: SparkJobStatusUpdate[]): {
    activeScopes: Set<string>;
    activePrefixes: Set<string>;
    pendingScopes: Set<string>;
} => {
    const latestStatus = new Map<string, { code?: string; stage_event?: string }>();
    updates.forEach(update => {
        if (!update.scope || update.scope === 'overall') {
            return;
        }
        latestStatus.set(update.scope, { code: update.code, stage_event: update.stage_event });
    });

    const activeScopes = new Set<string>();
    const activePrefixes = new Set<string>();
    const pendingScopes = new Set<string>();

    latestStatus.forEach((status, scope) => {
        if (status.code === 'in_progress') {
            activeScopes.add(scope);
            const parts = scope.split('.').filter(Boolean);
            for (let i = 1; i < parts.length; i++) {
                const prefix = parts.slice(0, i).join('.');
                if (prefix) {
                    activePrefixes.add(prefix);
                }
            }
        } else if (status.stage_event === 'stage_pending' && status.code === 'pending') {
            pendingScopes.add(scope);
        }
    });

    return { activeScopes, activePrefixes, pendingScopes };
};

/**
 * Build a map from normalized scope key to actual duration in seconds.
 * Uses stage_actual_seconds from completion events when available.
 * Falls back to calculating duration from start/completion timestamp pairs.
 * The key is normalized (run_agent filtered out) to match timeline entry scopeKeys.
 */
const buildScopeDurations = (updates: SparkJobStatusUpdate[]): Map<string, number> => {
    const durations = new Map<string, number>();
    const startTimestamps = new Map<string, number>();

    updates.forEach(update => {
        if (!update.scope) return;

        // Normalize scope key to match timeline entries (filter out run_agent)
        const { root, segments } = splitScopeSegments(update.scope);
        const normalizedKey = segments.length > 0 ? `${root}.${segments.join('.')}` : root;

        // Track start timestamps for fallback duration calculation
        if (update.code === 'in_progress' && !startTimestamps.has(normalizedKey)) {
            startTimestamps.set(normalizedKey, new Date(update.timestamp).getTime());
        }

        // Use stage_actual_seconds if available (preferred)
        if (update.stage_event === 'stage_completed' && update.stage_actual_seconds != null) {
            durations.set(normalizedKey, update.stage_actual_seconds);
        }
        // Fallback: calculate from start/completion timestamps
        else if (update.code === 'completed' && !durations.has(normalizedKey)) {
            const startMs = startTimestamps.get(normalizedKey);
            if (startMs != null) {
                const endMs = new Date(update.timestamp).getTime();
                const durationSec = Math.max(0, Math.floor((endMs - startMs) / 1000));
                durations.set(normalizedKey, durationSec);
            }
        }
    });
    return durations;
};

export const JobDetailModal: React.FC<JobDetailModalProps> = ({ isOpen, onClose, jobFqn }) => {
    const storeJob = useAtlasStore((state: AtlasState) =>
        jobFqn ? state.currentSparkJobs.find(j => j.fqn === jobFqn) : null
    );
    const setSparkUserInteractionBlockingRefresh = useAtlasStore((state: AtlasState) => state.setSparkUserInteractionBlockingRefresh);
    const cancelSparkJob = useAtlasStore((state: AtlasState) => state.cancelSparkJob);
    const isLoadingCancel = useAtlasStore((state: AtlasState) => state.isLoadingSpark.cancelJob);

    // State for cancel confirmation modal
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    // State for copy feedback
    const [copied, setCopied] = useState(false);
    // Ref for auto-scrolling timeline
    const timelineRef = useRef<HTMLDivElement>(null);
    // State for fetched job data (fallback when not in store)
    const [fetchedJob, setFetchedJob] = useState<SparkJob | null>(null);

    // Use store job if available, otherwise use fetched job
    const job = storeJob || fetchedJob;
    const displayStatus = getJobDisplayStatus(job) || job?._status || null;
    const isDisplayPending = displayStatus === 'pending';
    const isDisplayRunning = displayStatus === 'running';
    const isDisplayActive = isDisplayPending || isDisplayRunning;

    // Use animated progress that updates every second based on elapsed time
    const { progressPct, remainingSeconds } = useAnimatedProgress(job);
    const etaLabel = formatEta(remainingSeconds);

    // Fetch job data if not in store (for completed jobs)
    useEffect(() => {
        if (isOpen && jobFqn && !storeJob) {
            atlasClient.getSparkJob(jobFqn)
                .then(setFetchedJob)
                .catch(err => console.error('Failed to fetch job:', err));
        }
        if (!isOpen) {
            setFetchedJob(null);
        }
    }, [isOpen, jobFqn, storeJob]);

    // Effect to block/unblock background refresh
    useEffect(() => {
        if (isOpen) {
            setSparkUserInteractionBlockingRefresh(true);
            return () => {
                setSparkUserInteractionBlockingRefresh(false);
            };
        }
    }, [isOpen, setSparkUserInteractionBlockingRefresh]);

    // Reset copied state when modal opens/closes
    useEffect(() => {
        if (!isOpen) {
            setCopied(false);
        }
    }, [isOpen]);

    // Check if job can be cancelled (pending or running status)
    const canCancel = job && (job._status === 'pending' || job._status === 'running');

    // Get status updates sorted by timestamp (oldest first for timeline), limit to last 10
    const { visibleUpdates, rawUpdates } = useMemo(() => {
        if (!job?._status_updates || job._status_updates.length === 0) {
            return { visibleUpdates: [], rawUpdates: [] as SparkJobStatusUpdate[] };
        }
        const sorted = [...job._status_updates].sort(
            (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
        // Filter out stage completion breadcrumbs (code='completed') but keep job-level completion
        // (scope='overall' or no scope) to show final "Job completed" message.
        // Also filter out pending placeholder events (code='pending') - these are only used
        // to determine pending state via rawUpdates, not displayed as timeline entries.
        const filtered = sorted.filter(update =>
            (update.code !== 'completed' || !update.scope || update.scope === 'overall') &&
            update.code !== 'pending'
        );
        return {
            // Show all filtered entries - data is already bounded by backend (max 50 entries)
            // and the timeline has max-height with scroll
            visibleUpdates: filtered,
            rawUpdates: sorted,
        };
    }, [job?._status_updates]);

    const timelineEntries = useMemo(() => buildTimelineEntries(visibleUpdates), [visibleUpdates]);
    const { activeScopes, activePrefixes, pendingScopes } = useMemo(() => buildActiveScopes(rawUpdates), [rawUpdates]);
    const displayedActiveScopes = useMemo(
        () => (isDisplayActive ? activeScopes : new Set<string>()),
        [activeScopes, isDisplayActive]
    );
    const displayedActivePrefixes = useMemo(
        () => (isDisplayActive ? activePrefixes : new Set<string>()),
        [activePrefixes, isDisplayActive]
    );
    const displayedPendingScopes = useMemo(
        () => (isDisplayActive ? pendingScopes : new Set<string>()),
        [pendingScopes, isDisplayActive]
    );
    const scopeDurations = useMemo(() => buildScopeDurations(rawUpdates), [rawUpdates]);

    const timelineScrollLockedRef = useRef(false);
    const wasAtBottomRef = useRef(true);

    // Capture scroll position BEFORE render updates the DOM
    useLayoutEffect(() => {
        const el = timelineRef.current;
        if (!el) return;
        const threshold = 8;
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        wasAtBottomRef.current = distanceFromBottom <= threshold;
    }, [timelineEntries]);

    // Track whether the user has scrolled away from the bottom
    useEffect(() => {
        const el = timelineRef.current;
        if (!el) {
            return;
        }
        const handleScroll = () => {
            const threshold = 8;
            const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
            timelineScrollLockedRef.current = distanceFromBottom > threshold;
        };
        el.addEventListener('scroll', handleScroll);
        return () => el.removeEventListener('scroll', handleScroll);
    }, []);

    // Auto-scroll timeline to bottom only if user was at bottom before update
    useEffect(() => {
        const el = timelineRef.current;
        if (!el) return;
        if (wasAtBottomRef.current && !timelineScrollLockedRef.current) {
            el.scrollTop = el.scrollHeight;
        }
    }, [timelineEntries]);

    // Handler for cancel confirmation
    const handleCancelConfirm = async () => {
        if (!job) return;

        const success = await cancelSparkJob(job.fqn);
        if (success) {
            setShowCancelConfirm(false);
            onClose(); // Close the job detail modal after successful cancellation
        }
    };

    // Handler for copying logs to clipboard
    const handleCopyLogs = async () => {
        if (!job?._logs) return;

        try {
            await navigator.clipboard.writeText(job._logs);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy logs:', err);
        }
    };

    if (!isOpen || !job) {
        return null;
    }

    return (
        <>
        <div className={ModalStyles.modalOverlay} onClick={onClose}>
            <div className={`${ModalStyles.modalContent} ${styles.jobDetailModal}`} onClick={(e) => e.stopPropagation()}>
                {/* Header with status badge */}
                <div className={styles.modalHeader}>
                    <h2 className={styles.jobTitle}>{job.name || 'Job Details'}</h2>
                    <span className={`${styles.statusBadge} ${styles[`status-${displayStatus || job._status}`]}`}>
                        {displayStatus || job._status}
                    </span>
                </div>

                {/* Job info cards */}
                <div className={styles.infoCards}>
                    <div className={styles.infoCard}>
                        <span className={styles.infoLabel}>Created</span>
                        <span className={styles.infoValue}>{formatAbsoluteTime(job._created_at)}</span>
                    </div>
                    {job._completed_at && !isDisplayActive && (
                        <div className={styles.infoCard}>
                            <span className={styles.infoLabel}>Completed</span>
                            <span className={styles.infoValue}>{formatAbsoluteTime(job._completed_at)}</span>
                        </div>
                    )}
                </div>

                {/* Progress bar for running jobs - show if we have progress or ETA info */}
                {isDisplayRunning && (progressPct != null || job._expected_total_time_seconds != null) && (
                    <div className={styles.progressSection}>
                        {progressPct != null && (
                            <div className={styles.progressBar}>
                                <div
                                    className={styles.progressFill}
                                    style={{ width: `${Math.min(100, progressPct)}%` }}
                                />
                            </div>
                        )}
                        <div className={styles.progressInfo}>
                            {progressPct != null && <span>{Math.round(progressPct)}% complete</span>}
                            {/* Show remaining time from animated progress */}
                            {etaLabel && (
                                <span className={styles.timeRemaining}>{etaLabel}</span>
                            )}
                        </div>
                    </div>
                )}

                {/* Progress Timeline - show for running and completed jobs */}
                {timelineEntries.length > 0 && (
                    <div className={styles.timelineSection}>
                        <h3 className={styles.sectionTitle}>Progress</h3>
                        <div className={styles.timeline} ref={timelineRef}>
                            {timelineEntries.map((entry, index) => {
                                const isNested = entry.depth > 0;
                                const scopeKey = entry.scopeKey || '';
                                const isActiveScope = scopeKey && displayedActiveScopes.has(scopeKey);
                                const isActiveHeading = scopeKey && displayedActivePrefixes.has(scopeKey);
                                const isPendingScope = scopeKey && displayedPendingScopes.has(scopeKey);
                                const isActive = (entry.isHeading ? isActiveHeading : isActiveScope) || (!entry.isHeading && index === timelineEntries.length - 1 && isDisplayActive);
                                const isPending = !entry.isHeading && isPendingScope && !isActive;

                                return (
                                    <div
                                        key={index}
                                        className={`${styles.timelineItem} ${isNested ? styles.timelineItemNested : ''}`}
                                        style={{ paddingLeft: isNested ? `${entry.depth * 20}px` : undefined }}
                                    >
                                        <div className={styles.timelineDot}>
                                            <div className={`${styles.dot} ${isActive ? styles.dotActive : ''} ${isPending ? styles.dotPending : ''} ${isNested ? styles.dotNested : ''}`} />
                                            {index < timelineEntries.length - 1 && <div className={styles.timelineLine} />}
                                        </div>
                                        <div className={styles.timelineContent}>
                                            <div className={styles.timelineMessageRow}>
                                                <span className={`${styles.timelineMessage} ${entry.isHeading ? styles.timelineHeading : ''}`}>
                                                    {entry.label}
                                                </span>
                                            </div>
                                            {!entry.isHeading && (
                                                <span className={`${styles.timelineTime} ${isActive ? styles.timelineTimeActive : ''} ${isPending ? styles.timelineTimePending : ''}`}>
                                                    {isActive ? 'In progress…' : isPending ? 'Pending…' : (() => {
                                                        // Special case: show total job duration for "Job completed" entry
                                                        if (scopeKey === 'overall' && timelineEntries.length > 1) {
                                                            const firstEntry = timelineEntries[0];
                                                            const startMs = new Date(firstEntry.timestamp).getTime();
                                                            const endMs = new Date(entry.timestamp).getTime();
                                                            const totalSec = Math.max(0, Math.floor((endMs - startMs) / 1000));
                                                            return formatDuration(totalSec);
                                                        }
                                                        // Use actual duration from completion event if available
                                                        const actualDuration = scopeKey ? scopeDurations.get(scopeKey) : undefined;
                                                        if (actualDuration != null) {
                                                            return formatDuration(actualDuration);
                                                        }
                                                        // Fallback: show absolute time for last entry or entries without duration
                                                        const next = timelineEntries[index + 1];
                                                        if (!next) {
                                                            return formatTimelineTime(entry.timestamp);
                                                        }
                                                        // For entries without actual duration, calculate from next timestamp
                                                        const currentMs = new Date(entry.timestamp).getTime();
                                                        const nextMs = new Date(next.timestamp).getTime();
                                                        const diffSec = Math.max(0, Math.floor((nextMs - currentMs) / 1000));
                                                        return formatDuration(diffSec);
                                                    })()}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Empty state for no progress updates */}
                {timelineEntries.length === 0 && isDisplayPending && (
                    <div className={styles.emptyProgress}>
                        <ScheduleIcon className={styles.emptyIcon} />
                        <span>Waiting to start...</span>
                    </div>
                )}

                {timelineEntries.length === 0 && isDisplayRunning && (
                    <div className={styles.emptyProgress}>
                        <PlayCircleIcon className={styles.emptyIcon} />
                        <span>Processing...</span>
                    </div>
                )}

                {/* Actions */}
                <div className={styles.modalActions}>
                    {/* Copy Logs button - only show if logs exist */}
                    {job._logs && (
                        <button
                            onClick={handleCopyLogs}
                            className={`${styles.copyLogsButton} ${copied ? styles.copied : ''}`}
                            title="Copy raw logs to clipboard for debugging"
                        >
                            <ContentCopyIcon fontSize="small" />
                            {copied ? 'Copied!' : 'Copy Logs'}
                        </button>
                    )}

                    <div className={styles.actionsSpacer} />

                    <button onClick={onClose} className={ModalStyles.buttonSecondary}>
                        Close
                    </button>
                    {canCancel && (
                        <button
                            onClick={() => setShowCancelConfirm(true)}
                            className={`${ModalStyles.buttonPrimary} ${ModalStyles.buttonDestructive}`}
                            disabled={isLoadingCancel}
                        >
                            {isLoadingCancel ? 'Processing...' : 'Cancel Job'}
                        </button>
                    )}
                </div>
            </div>
        </div>

        {/* Cancel Confirmation Modal */}
        <ConfirmationModal
            isOpen={showCancelConfirm}
            onClose={() => setShowCancelConfirm(false)}
            onConfirm={handleCancelConfirm}
            title="Cancel Job?"
            message={`Are you sure you want to cancel the "${job.name || 'unnamed'}" job? This action cannot be undone.`}
            confirmText="Cancel Job"
            isConfirming={isLoadingCancel}
            variant="destructive"
        />
        </>
    );
};
